import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapDispatchToProps, MapStateToProps, connect } from 'react-redux';
import { BoundQueryMessagesHandler, IConversationReadData, IMessageUIData, MarkAsReadHandler, QueryMessagesHandler, markAsRead, queryMessages } from 'src/chat/redux';
import { MessageFetchType } from 'src/chat/message-types';
import Message from '../Message/Message';
import SpintrLoader from 'src/ui/components/Loader';
import { IActiveUserProfile } from 'src/profile/reducer';
import { createSelector } from 'reselect';
import { IApplicationState } from 'src/spintr/reducer';
import IsTyping from '../IsTyping/IsTyping';
import "./Messages.scss";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { Label, UnstyledButton } from 'src/ui';
import Visage2Icon from 'src/visage2/Visage2Icon/Visage2Icon';
import { localize } from 'src/l10n';
import IdleChecker from 'src/spintr/components/IdleChecker';

interface IDispatchProps {
    queryMessages: QueryMessagesHandler | BoundQueryMessagesHandler;
    markAsRead: MarkAsReadHandler;
}

interface IOwnProps {
    conversationId: number;
    initialLastRead?: { [id: number]: number };
    compact?: boolean;
    scrollToMessageId?: number;
    innerRef?: any;
}

interface IStateProps {
    currentUser: IActiveUserProfile;
    conversation: Spintr.IConversation;
    messages?: Spintr.IChatMessage[];
    isLoading?: boolean;
    hasMore?: boolean;
    virtuosoIndex?: number;
    isSmallViewMode?: boolean;
}

type Props = IDispatchProps & IOwnProps & IStateProps;

const take = 20;
const defaultVirtuosoIndex = 100000;
const defaultMessageItemHeight = 88;

interface IState {
    scrollTo?: {
        id: number,
        fetchUntilFound?: boolean
    },
    isInitialized: boolean;
    isLoading: boolean;
    displayScrollIndicator: boolean;
    displayGoToLatest: boolean;
    unreadLineMessageId: number;
}

const Messages = (props: Props) => {
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const scrollerRef = useRef<HTMLDivElement>(null);
    const messagesWithDates = useRef<number[]>([]);
    const messagesRef = useRef<Spintr.IChatMessage[]>([]);
    const atBottomRef = useRef<boolean>(true);
    const hasAppliedInitialScrollRef = useRef<boolean>(false);
    const shouldFollowOutputRef = useRef<boolean>(false);
    
    const initialTopMostItemIndexRef = useRef(
        props.scrollToMessageId || props.messages.length === 0
            ? undefined
            : {
                index: props.messages.length - 1,
                align: "end" as const
            }
    );
    
    const [state, setState] = useState<IState>({
        isInitialized: false,
        scrollTo: props.scrollToMessageId ?
            {
                id: props.scrollToMessageId,
                fetchUntilFound: true
            } :
            undefined,
        isLoading: false,
        displayScrollIndicator: false,
        displayGoToLatest: false,
        unreadLineMessageId: 0
    });

    const ui: { [id: number]: IMessageUIData } = useMemo(() => {
        const result: { [id: number]: IMessageUIData } = {};

        for (let i = 0; i < props.messages.length; i++) {
            const prev = props.messages[i - 1];
            const m = props.messages[i];
            const displayDate = messagesWithDates.current.includes(m.id) || !prev || m.date.getTime() - prev.date.getTime() > 3600000;
            const displayUnreadLine = state.unreadLineMessageId === m.id;

            if (displayDate) {
                messagesWithDates.current.push(m.id);
            }
            
            result[m.id] = {
                displayDate,
                displayUnreadLine
            }
        }
        
        for (let i = 0; i < props.messages.length; i++) {
            const prev = props.messages[i - 1];
            const m = props.messages[i];
            const next = props.messages[i + 1];

            const isTop = result[m.id].displayDate || !prev || prev.user.id !== m.user.id;
            
            result[m.id] = {
                ...result[m.id],
                isTop,
                isBottom: !next || result[next.id].displayDate || next.user.id !== m.user.id
            }
        }

        messagesWithDates.current = messagesWithDates.current.filter((value, index, array) => array.indexOf(value) === index);

        return result;
    }, [props.messages, state.unreadLineMessageId]);

    const readData: IConversationReadData = useMemo(() => {
        let readByAllMessageId = 0;
        let lastMessageId = 0;

        const readByAllId = Object.values(props.conversation?.lastRead || {}).map((id) => id).sort((a, b) => a < b ? -1 : 1)[0];

        for (const m of props.messages) {
            if (m.user.id !== props.currentUser.id) {
                continue;
            }

            if (m.id > 0 && m.id <= readByAllId) {
                readByAllMessageId = m.id;
            }

            lastMessageId = m.id;
        }

        return { readByAllMessageId, lastMessageId };
    }, [props.conversation?.lastRead, props.messages, props.currentUser.id]);

    const fetch = useCallback(() => {
        if (props.isLoading || state.isLoading || !props.hasMore || !state.isInitialized) {
            return;
        }
        
        const promise = (props.queryMessages as BoundQueryMessagesHandler)({
            conversationId: props.conversationId,
            fetchType: MessageFetchType.Messages,
            take: take,
            maxId: props.messages[0].id,
            flatMode: true
        });

        setState((s) => ({
            ...s,
            isLoading: true
        }));

        promise.then(() => {
            setState((s) => ({
                ...s,
                isLoading: false
            }));
        }).catch(() => {});
    }, [props.isLoading, props.hasMore, props.messages, props.queryMessages, props.conversationId, state.isLoading, state.isInitialized]);

    const initialFetch = useCallback(() => {
        const promise = (props.queryMessages as BoundQueryMessagesHandler)({
            conversationId: props.conversationId,
            fetchType: MessageFetchType.Messages,
            take: take,
            maxId: 0,
            flatMode: true
        });

        promise.then(() => {
            setState((s) => ({
                ...s,
                isInitialized: true
            }));
        }).catch(() => {});
    }, [props.queryMessages, props.conversationId]);

    const scrollToId = useCallback((id: number) => {
        if (!virtuosoRef.current) {
            return;
        }

        const message = props.messages.find(x => x.id === id);

        if (!message) {
            return;
        }

        requestAnimationFrame(() => {
            setTimeout(() => {
                const index = props.messages.indexOf(message);
                
                if (!virtuosoRef.current) {
                    return;
                }

                virtuosoRef.current.scrollToIndex({
                    index,
                    align: "start",
                    behavior: "smooth"
                });
            }, 500);
        });
    }, [props.messages]);

    const markAsRead = useCallback(() => {
        if (!props.conversation || !props.conversation.unread) {
            return;
        }

        props.markAsRead(props.conversation.id, props.conversation.unread);
    }, [props]);

    const followOutput = (atBottom: boolean) => {
        if (state.isLoading) {
            return false;
        }

        if (atBottom) {
            return "smooth";
        }

        return shouldFollowOutputRef.current ? "smooth" : false;
    }

    const handleScroll = useCallback(() => {
        if (!scrollerRef.current) {
            return;
        }

        const el = scrollerRef.current;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        const isFarFromBottom = distanceFromBottom > 350;

        setState((s) => {
            if (s.displayGoToLatest !== isFarFromBottom) {
                return { ...s, displayGoToLatest: isFarFromBottom };
            }
            return s;
        });
    }, []);

    const handleScrollerRef = useCallback((ref) => {
        if (scrollerRef.current) {
            scrollerRef.current.removeEventListener("scroll", handleScroll);
        }
        scrollerRef.current = ref;
        if (ref) {
            ref.addEventListener("scroll", handleScroll);
        }
    }, [handleScroll]);

    useEffect(() => {
        initialFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!state.scrollTo || !virtuosoRef.current) {
            return;
        }

        const message = props.messages.find(x => x.id === state.scrollTo.id);

        if (!message && state.scrollTo.fetchUntilFound) {
            return fetch();
        }
        
        scrollToId(state.scrollTo.id);

        setState((s) => ({
            ...s,
            scrollTo: undefined
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.scrollTo, props.messages, props.hasMore]);

    useEffect(() => {
        if (props.innerRef) {
            props.innerRef.current = {
                scrollTo: (id: number) => {
                    setState((s) => ({
                        ...s,
                        scrollTo: {
                            id,
                            fetchUntilFound: true
                        },
                    }));
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const previousMessages = messagesRef.current;
        const previousLastMessage = previousMessages[previousMessages.length - 1];
        const currentLastMessage = props.messages[props.messages.length - 1];

        shouldFollowOutputRef.current =
            !!previousLastMessage &&
            !!currentLastMessage &&
            previousLastMessage.id !== currentLastMessage.id &&
            currentLastMessage.user.id === props.currentUser.id;
            
        // Show scroll to new messages indicator if we get a new message 
        // from someone else and we are not at the bottom.
        if (props.messages &&
            props.messages.length > 0 &&
            props.messages[props.messages.length - 1].user.id !== props.currentUser.id &&
            previousMessages &&
            previousMessages.length > 0 &&
            (currentLastMessage?.id ?? 0) !== (previousLastMessage?.id ?? 0)
        ) {
            setState((s) => ({
                ...s,
                displayScrollIndicator: !atBottomRef.current,
                unreadLineMessageId: s.unreadLineMessageId ? 
                    s.unreadLineMessageId : 
                    props.messages.length > 1 ? props.messages[props.messages.length - 2].id : 0
            }));
        }

        if (props.initialLastRead &&
            (props.messages?.length ?? 0) > 0
        ) {
            markAsRead();
        }

        // Scroll to bottom if we get a new message and close to bottom.
        // displayGoToLatest and displayScrollIndicator are false if we are close.
        if (previousLastMessage &&
            currentLastMessage &&
            previousLastMessage.id !== currentLastMessage.id &&
            !state.displayGoToLatest && 
            !state.displayScrollIndicator && 
            virtuosoRef.current
        ) {
            virtuosoRef.current.scrollToIndex({
                index: props.messages.length - 1,
                align: "end",
                behavior: "smooth"
            });
        }

        // Disable initial scroll if we receive new message
        if (previousLastMessage &&
            currentLastMessage &&
            previousLastMessage.id !== currentLastMessage.id
        ) {
            hasAppliedInitialScrollRef.current = true;
        }

        // Add new messages line and scroll to it if 
        // it hasn't been scrolled into view yet.
        const lastReadId = props.initialLastRead?.[props.currentUser.id] ?? 0;
        
        if (!hasAppliedInitialScrollRef.current &&
            !props.scrollToMessageId &&
            lastReadId &&
            currentLastMessage &&
            lastReadId < currentLastMessage.id) 
        {
            setState((s) => ({
                ...s,
                unreadLineMessageId: lastReadId,
                scrollTo: {
                    id: lastReadId,
                    fetchUntilFound: true
                }
            }));

            hasAppliedInitialScrollRef.current = true;
        }
        
        messagesRef.current = props.messages;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.messages, props.currentUser.id, props.initialLastRead]);

    return (
        <div className="Messages">
            <IdleChecker
                idleLimitMs={300000}
                onActivityAfterIdle={() => {
                    fetch()
                }} />
            <Virtuoso
                ref={virtuosoRef}
                scrollerRef={handleScrollerRef}
                style={{ flex: 1 }}
                increaseViewportBy={{ top: 0, bottom: 500 }}
                overscan={500}
                computeItemKey={(_, message) => message.id}
                defaultItemHeight={defaultMessageItemHeight}
                initialTopMostItemIndex={initialTopMostItemIndexRef.current}
                alignToBottom
                followOutput={followOutput}
                itemContent={(index: number, message: Spintr.IChatMessage, context: any) => {
                    return (
                        <Message
                            key={message.id}
                            data-id={message.id}
                            isSmallViewMode={props.isSmallViewMode}
                            currentUserId={props.currentUser.id}
                            currentUserName={props.currentUser.name}
                            conversation={props.conversation}
                            compact={props.compact}
                            message={message}
                            uiData={ui[message.id]}
                            isLast={props.messages.length > 0 && message.id === props.messages[props.messages.length - 1].id}
                            readData={readData}
                            highlight={props.scrollToMessageId === message.id}
                            scrollToMessage={(id: number) => {
                                setState((s) => ({
                                    ...s,
                                    scrollTo: {
                                        id,
                                        fetchUntilFound: true
                                    }
                                }));
                            }} />
                    )
                }}
                data={props.messages}
                firstItemIndex={props.virtuosoIndex ?? defaultVirtuosoIndex}
                startReached={fetch}
                atBottomStateChange={(atBottom: boolean) => {
                    atBottomRef.current = atBottom;

                    if (state.displayScrollIndicator && atBottom) {
                        setState((s) => ({
                            ...s,
                            displayScrollIndicator: false
                        }));
                    }
                }}
                components={{
                    Header: () => props.hasMore ? <SpintrLoader /> : null,
                    Footer: () => <IsTyping
                        conversationId={props.conversationId}
                        onVisible={() => {
                            setTimeout(() => {
                                if (atBottomRef.current) {
                                    virtuosoRef.current.scrollTo({ top: 1000000 });
                                }
                            }, 100);
                        }} />
                }}
            />
            {state.displayScrollIndicator && (
                <UnstyledButton
                    className="scroll-indicator primaryBGColor"
                    onClick={() => {
                        virtuosoRef.current.scrollTo({ top: 1000000, behavior: "smooth" });
                        setState((s) => ({
                            ...s,
                            displayScrollIndicator: false
                        }));
                    }}>
                    <Visage2Icon icon="arrow-down" color="white" />
                    <Label color="white" weight="medium">{localize("SHOW_NEW_MESSAGES")}</Label>
                </UnstyledButton>
            )}
            {!state.displayScrollIndicator && state.displayGoToLatest && (
                <UnstyledButton
                    className="scroll-indicator primaryBGColor"
                    onClick={() => {
                        virtuosoRef.current.scrollTo({ top: 1000000, behavior: "smooth" });
                    }}>
                    <Visage2Icon icon="arrow-down" color="white" />
                    <Label color="white" weight="medium">{localize("GO_TO_LATEST")}</Label>
                </UnstyledButton>
            )}
        </div>
    )
}

const messagesSelector = createSelector(
    [
        (state: IApplicationState) => state.chat.messages.items,
        (_, ownProps: IOwnProps) => ownProps.conversationId
    ],
    (messages, conversationId) => messages.filter(x => x.conversationId == conversationId)
)

const conversationSelector = createSelector(
    [
        (state: IApplicationState) => state.chat.conversations.items,
        (_, ownProps: IOwnProps) => ownProps.conversationId
    ],
    (conversations, conversationId) => conversations.find(x => x.id == conversationId)
)

const mapStateToProps: MapStateToProps<IStateProps, IOwnProps, Spintr.AppState> =
    (state, ownProps) => ({
        currentUser: state.profile.active,
        conversation: conversationSelector(state, ownProps),
        messages: messagesSelector(state, ownProps),
        isLoading: state.chat.messages.isLoading[ownProps.conversationId],
        hasMore: state.chat.messages.hasMore[ownProps.conversationId],
        virtuosoIndex: state.chat.messages.virtuosoIndex[ownProps.conversationId],
        isSmallViewMode: state.ui.isSmallViewMode,
    });

const mapDispatchToProps: MapDispatchToProps<IDispatchProps, IOwnProps> = {
    queryMessages,
    markAsRead,
};

export default connect(mapStateToProps, mapDispatchToProps)(Messages);
